import clsx from 'clsx';

import Button from 'molecules/Button';
import Image from 'molecules/Image';
import Link from 'molecules/Link';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import { resourceCardStyle } from 'organisms/Cards/Resource/styles';

import { hasArrayValues } from 'utils/array/arrays';

import type { FeaturedPartnersConfig } from 'components/FeaturedPartners/config';
import type { FC } from 'react';

const FeaturedPartnersCards: FC<FeaturedPartnersConfig> = props => {
  const { sectionProps, rest } = splitSectionProps(props);
  const { partners, heading } = rest;

  const hasPartners = hasArrayValues(partners);

  return (
    <Section {...sectionProps}>
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between gap-6 border-b border-gray-300 pb-4 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{heading}</h2>
        </div>

        {hasPartners && (
          <div className="flex w-full justify-center">
            <div className="flex flex-wrap justify-center gap-8">
              {partners?.map((partner, idx) => {
                const href = partner.button?.href || '#';
                const label = partner.button?.label || 'Learn More';

                return (
                  <Link
                    key={partner.id || idx}
                    href={href}
                    aria-label={partner.name || label}
                    className={clsx(resourceCardStyle({ isDark: false, isDarkGradient: true }), 'max-w-[400px] flex-1')}
                    enablePreview={false}
                  >
                    {partner.image?.file?.url && (
                      <div className="overflow-hidden rounded-lg">
                        <div className="flex h-52 w-full items-center justify-center rounded-lg bg-white">
                          <Image
                            url={partner.image.file.url}
                            width={200}
                            height={120}
                            noFill
                            className="max-h-28 w-auto object-contain"
                            alt={partner.image.alt || partner.name || ''}
                            sizes="200px"
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex grow flex-col justify-between gap-4">
                      <div className="flex flex-col gap-4">
                        <span className="line-clamp-3 text-xl font-bold text-white">{partner.name}</span>
                        {partner.summary && (
                          <span className="line-clamp-2 text-md text-gray-300">{partner.summary}</span>
                        )}
                      </div>
                      <div className="dark">
                        {/* The whole card is already the link, so the button is
                            only its visual cue. Rendering it with the partner's
                            href nested an <a> inside the card's <a>: invalid
                            HTML the browser splits apart, which broke hydration
                            on /build-partners/. Same pattern as FeaturedResourceCard. */}
                        <Button
                          hierarchy="linkColor"
                          endIcon="arrow-up-right"
                          {...partner.button}
                          href={undefined}
                          label={label}
                          semanticFallback="div"
                        />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

export default FeaturedPartnersCards;
